import {
  binDirections,
  fitSupport,
  planeAt,
  probeRing,
  type SupportPlane,
  type SurfaceProbeInput,
} from './surface-probe'

/**
 * Boxes that sit and slide on whatever the current depth frame says is there.
 *
 * Not rigid bodies. `(x, z)` is integrated; `y` is derived from the support fit
 * and hard-clamped. That removes bounce, tunnelling and the ballistic/contact
 * state machine while still testing the two things that matter — does the
 * sensed surface have the right slope, and does a sensed bump block and push.
 *
 * A ball was tried first and rolled out of frame before anything could be read
 * off it. A box with a friction cone stays where it is put until something
 * actually moves it, which is what makes the collision readable.
 *
 * Deliberately memoryless: one frame's geometry and nothing else. Accumulating
 * a world height field would make this a different demo — see docs/LIMITS.md.
 *
 * `BodyState` is mutated in place through the substep loop. That is the one
 * deliberate exception to the immutable style in this codebase: the solver runs
 * up to four substeps per frame at 30 fps over every body, and rebuilding the
 * state objects per substep is allocation churn with no readability gain. Every
 * function that mutates a body says so in its name or its doc.
 */

export type BodyMode = 'rest' | 'sliding' | 'blocked' | 'unsupported'

export interface BodyState {
  x: number
  y: number
  z: number
  vx: number
  vz: number
  mode: BodyMode
  /** Metres travelled since spawn — the headline "did it actually move" number. */
  travel: number
  /** Half-extent in XZ. Doubles as the sensed-obstacle probe radius. */
  half: number
  /** Highest obstacle seen last substep. The advancing-front term differentiates it. */
  peak: number
  /** Support-plane normal, XZ components, for orienting the mesh to the surface. */
  normalX: number
  normalZ: number
  /**
   * Snap `y` to the surface on the next substep instead of climbing to it. Set
   * on spawn, where the climb clamp would otherwise take a second to seat a box.
   */
  settle: boolean
}

export interface BodyWorld {
  bodies: BodyState[]
  /** Leftover frame time owed to the fixed-rate integrator, shared by all bodies. */
  accumulator: number
}

export type PushMode = 'off' | 'resolve' | 'shove'

/** Immutable at its boundary: the controls rebuild it rather than mutate it. */
export interface BodyTuning {
  /** Watchability only; noise and slope accelerations both scale with it. */
  readonly gravity: number
  /**
   * Tangent of the static friction angle. A box on a slope shallower than this
   * does not move at all — which is what stops measured slope noise from
   * walking it across the table over a minute.
   */
  readonly staticSlope: number
  /** Coulomb kinetic friction, dimensionless. */
  readonly kineticMu: number
  /** Viscous damping, s⁻¹. Kills the last millimetre-per-second of creep. */
  readonly damping: number
  /** Above the ~6 mm phantom-relief band, below the reference object. */
  readonly climbMetres: number
  readonly supportRadius: number
  readonly probeDirections: number
  /** Clamp on the advancing-front term, m/s. */
  readonly maxPushSpeed: number
  /** Positional de-penetration per substep, metres. */
  readonly maxCorrection: number
  readonly substepSeconds: number
  readonly pushMode: PushMode
  /** Holds every box still so the fit metrics can be read without them wandering. */
  readonly frozen: boolean
  /** Slope subtracted from the measured one, so a tilted table can be zeroed. */
  readonly biasSlopeX: number
  readonly biasSlopeZ: number
}

export interface StepReport {
  ms: number
  unsupported: number
  /** Substeps where a body fell back to the analytic floor for want of cells. */
  fallback: number
  /** Times the climb clamp fired. High means a box is being buried. */
  climbBlocked: number
  maxPenetration: number
  maxShove: number
  /** Tallest sensed obstacle on any box's ring, metres. NaN when nothing probed. */
  maxBump: number
  /** Support fit of the last-dropped box, for the stats panel. */
  focus: SupportPlane | null
  resting: number
  sliding: number
  blocked: number
}

/**
 * Frame times on device are 15–27 ms and variable; a variable-dt explicit
 * integrator with penetration correction oscillates. Four substeps at 1/120 s
 * covers a 33 ms frame, and dropping the remainder past that is preferable to a
 * spiral when the depth read stalls.
 */
const MAX_SUBSTEPS = 4

/** Below this a box counts as parked, and static friction may lock it. */
const REST_SPEED = 0.01

/** Box-on-box is a dull thud, not a click. Equal masses, so this is the whole model. */
const RESTITUTION = 0.1

const CONTACT_PASSES = 2

export function createBody(x: number, y: number, z: number, half: number): BodyState {
  return {
    x,
    y,
    z,
    vx: 0,
    vz: 0,
    mode: 'rest',
    travel: 0,
    half,
    peak: 0,
    normalX: 0,
    normalZ: 0,
    settle: true,
  }
}

export function stepBodies(
  world: BodyWorld,
  probe: SurfaceProbeInput,
  tuning: BodyTuning,
  deltaSeconds: number,
  ring: Float32Array
): StepReport {
  const started = performance.now()
  const report: StepReport = {
    ms: 0,
    unsupported: 0,
    fallback: 0,
    climbBlocked: 0,
    maxPenetration: 0,
    maxShove: 0,
    maxBump: Number.NaN,
    focus: null,
    resting: 0,
    sliding: 0,
    blocked: 0,
  }

  world.accumulator += Math.min(deltaSeconds, 0.25)

  let taken = 0
  while (world.accumulator >= tuning.substepSeconds && taken < MAX_SUBSTEPS) {
    world.accumulator -= tuning.substepSeconds
    taken += 1
    substep(world.bodies, probe, tuning, ring, report)
  }
  if (taken === MAX_SUBSTEPS) world.accumulator = 0

  for (const body of world.bodies) {
    if (body.mode === 'unsupported') report.unsupported += 1
    else if (body.mode === 'blocked') report.blocked += 1
    else if (body.mode === 'sliding') report.sliding += 1
    else report.resting += 1
  }

  report.ms = performance.now() - started
  return report
}

function substep(
  bodies: BodyState[],
  probe: SurfaceProbeInput,
  tuning: BodyTuning,
  ring: Float32Array,
  report: StepReport
): void {
  const supports: (SupportPlane | null)[] = []

  for (const body of bodies) {
    let support = fitSupport(
      probe.positions,
      probe.valid,
      probe.cells,
      body.x,
      body.z,
      tuning.supportRadius
    )
    if (!support && probe.ground) {
      support = planeAt(probe.ground, body.x, body.z)
      report.fallback += 1
    }
    supports.push(support)

    if (!support) {
      body.mode = 'unsupported'
      body.vx = 0
      body.vz = 0
      body.peak = 0
      continue
    }

    body.normalX = -support.slopeX
    body.normalZ = -support.slopeZ

    const startX = body.x
    const startZ = body.z
    integrate(body, support, tuning)
    resolveSensed(body, probe, tuning, support, ring, report)
    body.travel += Math.hypot(body.x - startX, body.z - startZ)
  }

  for (let pass = 0; pass < CONTACT_PASSES; pass += 1) resolveContacts(bodies)

  for (let i = 0; i < bodies.length; i += 1) {
    const support = supports[i]
    const body = bodies[i]
    if (!support || !body) continue
    report.climbBlocked += settleHeight(body, support, tuning)
  }

  report.focus = supports.length > 0 ? (supports[supports.length - 1] ?? null) : null
}

function integrate(body: BodyState, support: SupportPlane, tuning: BodyTuning): void {
  if (tuning.frozen) {
    body.vx = 0
    body.vz = 0
    return
  }

  const dt = tuning.substepSeconds
  const driveX = support.slopeX - tuning.biasSlopeX
  const driveZ = support.slopeZ - tuning.biasSlopeZ
  const speed = Math.hypot(body.vx, body.vz)

  // Inside the friction cone a parked box stays parked. Without this the ~0.6°
  // of residual fit noise integrates into a slow walk across the table.
  if (speed < REST_SPEED && Math.hypot(driveX, driveZ) <= tuning.staticSlope) {
    body.vx = 0
    body.vz = 0
    return
  }

  if (speed > 1e-6) {
    const shed = tuning.gravity * tuning.kineticMu * dt
    const scale = Math.max(0, 1 - shed / speed)
    body.vx *= scale
    body.vz *= scale
  }

  body.vx -= tuning.gravity * driveX * dt
  body.vz -= tuning.gravity * driveZ * dt

  const decay = Math.exp(-tuning.damping * dt)
  body.vx *= decay
  body.vz *= decay

  body.x += body.vx * dt
  body.z += body.vz * dt
}

function resolveSensed(
  body: BodyState,
  probe: SurfaceProbeInput,
  tuning: BodyTuning,
  support: SupportPlane,
  ring: Float32Array,
  report: StepReport
): void {
  const previousPeak = body.peak
  body.peak = 0

  if (tuning.pushMode === 'off') {
    body.mode = Math.hypot(body.vx, body.vz) > REST_SPEED ? 'sliding' : 'rest'
    return
  }

  probeRing(probe.positions, probe.valid, probe.cells, body.x, body.z, body.half, support, ring)

  const bins = ring.length
  const directions = binDirections(bins)
  let normalX = 0
  let normalZ = 0
  let peak = 0

  for (let bin = 0; bin < bins; bin += 1) {
    const height = ring[bin]!
    if (Number.isNaN(height)) continue
    if (Number.isNaN(report.maxBump) || height > report.maxBump) report.maxBump = height

    const depth = height - tuning.climbMetres
    if (depth <= 0) continue

    normalX -= directions[bin * 2]! * depth
    normalZ -= directions[bin * 2 + 1]! * depth
    if (depth > peak) peak = depth
  }

  body.peak = peak
  if (peak > 0) body.mode = 'blocked'
  else body.mode = Math.hypot(body.vx, body.vz) > REST_SPEED ? 'sliding' : 'rest'

  const length = Math.hypot(normalX, normalZ)
  if (length < 1e-9) return
  normalX /= length
  normalZ /= length

  if (peak > report.maxPenetration) report.maxPenetration = peak

  const correction = Math.min(peak, tuning.maxCorrection)
  body.x += normalX * correction
  body.z += normalZ * correction

  const along = body.vx * normalX + body.vz * normalZ
  if (along < 0) {
    body.vx -= normalX * along
    body.vz -= normalZ * along
  }

  if (tuning.pushMode !== 'shove') return

  // The *rate* term is what reads as a shove rather than a nudge, and it only
  // needs the obstacle to be getting taller nearby — which matters, because a
  // 25 mm wall at ~9.5 mm cell spacing is 1–2 cells wide and will not survive
  // as a clean surface.
  const rate = (peak - previousPeak) / tuning.substepSeconds
  const speed = Math.min(Math.max(rate, 0), tuning.maxPushSpeed)
  if (speed <= 0) return

  body.vx += normalX * speed
  body.vz += normalZ * speed
  if (speed > report.maxShove) report.maxShove = speed
}

/**
 * Box-vs-box in XZ only. Yaw is never integrated, so the footprints stay
 * world-axis-aligned and a min-penetration-axis AABB resolve is exact rather
 * than an approximation of one.
 */
function resolveContacts(bodies: BodyState[]): void {
  for (let i = 0; i < bodies.length; i += 1) {
    const a = bodies[i]
    if (!a || a.mode === 'unsupported') continue

    for (let j = i + 1; j < bodies.length; j += 1) {
      const b = bodies[j]
      if (!b || b.mode === 'unsupported') continue

      const reach = a.half + b.half
      const dx = b.x - a.x
      const dz = b.z - a.z
      const overlapX = reach - Math.abs(dx)
      const overlapZ = reach - Math.abs(dz)
      if (overlapX <= 0 || overlapZ <= 0) continue

      if (overlapX < overlapZ) {
        const sign = dx < 0 ? -1 : 1
        a.x -= sign * overlapX * 0.5
        b.x += sign * overlapX * 0.5
        if ((b.vx - a.vx) * sign < 0) {
          const [va, vb] = exchange(a.vx, b.vx)
          a.vx = va
          b.vx = vb
        }
      } else {
        const sign = dz < 0 ? -1 : 1
        a.z -= sign * overlapZ * 0.5
        b.z += sign * overlapZ * 0.5
        if ((b.vz - a.vz) * sign < 0) {
          const [va, vb] = exchange(a.vz, b.vz)
          a.vz = va
          b.vz = vb
        }
      }
    }
  }
}

/** 1-D collision between equal masses. */
function exchange(va: number, vb: number): [number, number] {
  const sum = va + vb
  const spread = (vb - va) * RESTITUTION
  return [(sum + spread) * 0.5, (sum - spread) * 0.5]
}

/**
 * The single most important line in the solver is the rise clamp: a height
 * demand larger than `climbMetres` is a wall, not ground, and letting `y` follow
 * it is exactly how a box gets swallowed as a real object arrives.
 *
 * Returns 1 when the clamp fired.
 */
function settleHeight(body: BodyState, support: SupportPlane, tuning: BodyTuning): number {
  const target = support.height + body.half

  if (body.settle) {
    body.y = target
    body.settle = false
    return 0
  }

  const rise = target - body.y
  if (rise > tuning.climbMetres) {
    body.y += tuning.climbMetres
    return 1
  }

  body.y = target
  return 0
}
